/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { useOnboarding } from '../../../../contexts/onboarding-context';
import { useTypewriter } from '../hooks/use-typewriter';
import { AudioPlayer } from './audio-player';
import {
  Question,
  MailanderUserLogo,
  QuestionText,
  LogoAndListenWrapper,
} from '../styles/question';
import { fadeInAnimation, fadeUpAnimation } from '../../animations';

const URL_REGEX =
  /(?:https?:\/\/|www\.)[^\s<>"'`,;!?\])]*[^\s<>"'`,;!?\].):]|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+(?:com|org|net|io|co|dev|app|me|info|biz|edu|gov|xyz|ai|tech|uk|de|fr|nl|be|at|ch|es|it|pt|pl|cz|se|no|dk|fi|ru|jp|cn|kr|in|au|nz|ca|us|br|mx|ar|za)(?:\/[^\s<>"'`,;!?\])]*[^\s<>"'`,;!?\].):"])?/gi;

interface QuestionContentProps {
  questionText: string;
  audioId?: string | undefined;
  typewriterActive?: boolean | undefined;
}

export const QuestionContent: React.FC<QuestionContentProps> = ({
  questionText,
  audioId,
  typewriterActive = false,
}) => {
  const { userProfile: profile } = useOnboarding();

  // Get user's first name or fallback to a default
  const firstName = profile?.first_name || profile?.nickname || 'there';

  const displayText = questionText;

  // Typewriter effect for smooth question transitions
  const { displayText: typewriterText } = useTypewriter(displayText, typewriterActive, 2000);

  const renderHtml = useMemo(() => {
    const textToRender = typewriterActive ? typewriterText : displayText;
    if (!textToRender) return '';

    const escapeHtml = (str: string) =>
      str
        ?.replace(/&/g, '&amp;')
        ?.replace(/</g, '&lt;')
        ?.replace(/>/g, '&gt;')
        ?.replace(/"/g, '&quot;')
        ?.replace(/'/g, '&#39;');

    const replaced = textToRender?.replace(/\$\{nickname\}|\{nickname\}/g, escapeHtml(firstName));
    const withBreaks = replaced?.replace(/\r\n|\r|\n/g, '<br/>');

    // Make URLs clickable
    URL_REGEX.lastIndex = 0;
    const withLinks = withBreaks?.replace(URL_REGEX, (url) => {
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#5E84ED;text-decoration:underline;cursor:pointer;word-break:break-all;">${url}</a>`;
    });

    return withLinks;
  }, [displayText, typewriterText, typewriterActive, firstName]);

  return (
    <Question>
      <LogoAndListenWrapper css={fadeInAnimation(0.4, 0.2)}>
        <MailanderUserLogo
          src="/icons/onboarding/question/mailander-user-logo.svg"
          alt="Mailander User Logo"
        />

        {audioId && (
          <AudioPlayer audioId={audioId} />
        )}
      </LogoAndListenWrapper>

      <div css={fadeUpAnimation(0.45, 0.3)}>
        <QuestionText dangerouslySetInnerHTML={{ __html: renderHtml }} />
      </div>
    </Question>
  );
};
